import React, { useEffect, useRef, useState } from "react";
import './AddPackages.css';
import OdooService from '../../../services/odooService';
import { useNavigate, useLocation } from "react-router-dom";
import ErrorModal from "../../../components/ErrorModal/ErrorModal";
import LoadingOverlay from "../../../components/LoadingOverlay/LoadingOverlay";

const PackagesScan = ({Location, onPackagesScanned}) => {
    const navigate = useNavigate();
    const handleBack = () => navigate(-1);
    const [user, setUser] = useState(null);
    const [packages, setPackages] = useState([]);
    const [currentScan, setCurrentScan] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);
    const scanInputRef = useRef(null);
    const scanTimer = useRef(null);
    const location = useLocation();
    const { location_id, location_name, package_id, package_name } = location.state || {};
    const [infoMessage, setInfoMessage] = useState('');
    const [infoType, setInfoType] = useState('');

    useEffect(() => {
        if (scanInputRef.current) scanInputRef.current.focus();
    }, []);

    useEffect(() => {
        if (error === "" && scanInputRef.current) {
            setTimeout(() => scanInputRef.current.focus(), 50);
        }
    }, [error, packages, currentScan]);

    useEffect(() => {
            const last = localStorage.getItem("lastScan");
    
            if (package_name && last !== package_name) {
                validatePackage(package_name);
                localStorage.setItem("lastScan", package_name);
            }
        }, [package_name]);

    const handleManualEnter = async (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const code = inputRef.current.value.trim();
            if (!code) return;

            await validatePackage(code);
            inputRef.current.value = "";
        }
    };
    
    const handleScannerChange = (e) => {
        const value = e.target.value;
        setCurrentScan(value);

        if (scanTimer.current) clearTimeout(scanTimer.current);

        if (value.trim() !== "") {
            scanTimer.current = setTimeout(() => {
                simulateEnter();
            }, 500);
        }
    };

    const handleScannerKeyEvents = (e) => {
        if (["Backspace", "BrowserBack", "Enter"].includes(e.key) || e.key.length > 1) {
            e.preventDefault();
        }
    };

    const simulateEnter = () => {
        const code = scanInputRef.current?.value.trim();
        if (!code) return; 
        if (!packages.some(pkg => pkg.name === code)) {
            validatePackage(code);
        } else {
            setInfoMessage('Este paquete ya está en la lista');
            setInfoType('error');
            setTimeout(() => { 
                setInfoMessage('');
            }, 2000);
        } if (scanInputRef.current) { 
            scanInputRef.current.focus();
            scanInputRef.current.select();
            } 
    };

    const validatePackage = async (packageBarcode) => {
        setLoading(true);
        setError('');
        const barcode = packageBarcode.trim();
        try {
            const barcode_result = await OdooService.detectBarcode(barcode, location_id);

            if (!barcode_result.success) {
                setLoading(false);
                setError(barcode_result.message);
                return;
            }

            if (barcode_result.type === 'package') {
                const package_result = await OdooService.validatePackageAndAddList(packageBarcode, location_id);
                if (package_result.success) {
                    const newPackage = {
                        id: package_result.package.id,
                        name: package_result.package.name,
                    };
                    setPackages(prev => {
                        if (prev.some(p => p.id === newPackage.id)) {
                            setError('Ese paqute ya está en la lista.');
                            setLoading(false);
                            return prev;
                        }
                        setLoading(false);
                        return [...prev, newPackage];
                    });
                    setCurrentScan("");
                    if (scanInputRef.current) {
                        scanInputRef.current.focus();
                        scanInputRef.current.select();
                    }
                    setError('');
                    return;
                } else {
                    setError(package_result.message);
                    setLoading(false);
                    return;
                }
            }

            if (barcode_result.type === 'location') {
                const session = JSON.parse(localStorage.getItem("odoo_session"));
                const userId = session?.userId.uid;
                const destiny_id = barcode_result.data.destiny_id;
                const destiny_result = await OdooService.internalTransfer(userId, location_id, packages, destiny_id);
                if (destiny_result.success) {
                    navigate('/home')
                } else {
                    setError(destiny_result.message);
                    setLoading(false);
                    return;
                }
            }

            else {
                setError("El código no corresponde a paquete ni a ubicación.");
                setLoading(false);
                return;
            }
        } catch (err) {
            setError('Error de conexión: ' + err.message);
            setLoading(false);
            if (scanInputRef.current) {
                scanInputRef.current.focus();
            }
        }
    };

    const removePackage = (packageId) => {
        setPackages(prev => prev.filter(pkg => pkg.id !== packageId))
    };

    return (
        <div className="inventory-step">
            <header className="inventory-header">
            <h1 className="inventory-title">Transferencia Interna</h1>
            <p className="inventory-subtitle">Escanear Paquetes y Transferir</p>
            </header>

            <div className="inventory-content">
                <div className="inventory-form">

                    <div className="inventory-location">
                        <strong>Ubicación:</strong> {location_name}
                    </div>

                    <div className="inventory-input-group">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Agregar paquete manualmente..."
                            onKeyDown={handleManualEnter}
                            inputMode="text"
                            className="inventory-input"
                            disabled={loading}
                            onFocus={(e) => e.target.select()}
                        />

                        <input
                            ref={scanInputRef}
                            type="text"
                            value={currentScan}
                            onChange={handleScannerChange}
                            onKeyDown={handleScannerKeyEvents}
                            autoFocus
                            inputMode="none"
                            className="inventory-scan-input"
                        />
                    </div>

                    <div className="inventory-infoMessage">
                        {infoMessage && (
                            <div
                                className={`scanner-info ${
                                    infoType === 'success' ? 'scanner-info-success' : 'scanner-info-error'
                                }`}
                            >
                                {infoMessage}
                            </div>
                        )}
                    </div>

                    <div className="inventory-summary">
                        <h3>Paquetes escaneados: {packages.length}</h3>
                    </div>

                    <div className="inventory-list-wrapper">
                        {packages.length > 0 ? (
                            <div className="inventory-list">
                            {packages.map((pkg) => (
                                <div key={pkg.id} className="inventory-item">
                                <div className="inventory-item-details">
                                    <strong>{pkg.name}</strong>
                                </div>
                                <button
                                    onClick={() => removePackage(pkg.id)}
                                    className="inventory-remove-btn"
                                >
                                    ✕
                                </button>
                                </div>
                            ))}
                            </div>
                        ) : (
                            <div className="inventory-empty">
                            <p>No hay paquetes escaneados</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="inventory-actions">
                <button
                    onClick={handleBack}
                    className="btn-inventory btn-secondary-inventory"
                >
                    Volver
                </button>
            </div>

            {loading && <LoadingOverlay message="Cargando..." />}

            <ErrorModal message={error} onClose={() => setError("")} />
        </div>
    );
};

export default PackagesScan;